import { Fragment, useEffect, useState } from "react";
import { FaArrowLeft, FaTrash, FaRegCommentDots } from "react-icons/fa";
import { MdMore } from "react-icons/md";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import TimeAgo from "react-timeago";

import { delete_reply, getPostReplies } from "../api";
import { getMediaUrl } from "../lib/utils";

import CommentPhoto from "./comments/CommentPhoto";
import CommentVideo from "./comments/CommentVideo";
import CommentVoice from "./comments/CommentVoice";

import ToastMessage from "./ToastMessage";

const ShowReply = ({ comment, closeShowReply }) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!comment?.id) return;

    getPostReplies(comment.id)
      .then((res) => setData(res.data || []))
      .catch((error) => console.error("Get replies error:", error));
  }, [comment?.id]);

  const deleteReply = (id) => {
    const previousData = data;

    setData((prev) => prev.filter((reply) => reply?.reply_id !== id));

    delete_reply(id)
      .then((res) => {
        if (res.data.message) ToastMessage(res.data.message);
      })
      .catch((error) => {
        console.error("Delete reply error:", error);
        setData(previousData);
        ToastMessage(error?.response?.data?.message || "Unable to delete reply.");
      });
  };

  return (
    <div className="supportroll absolute inset-0 z-[120] flex h-full flex-col bg-white">

      {/* HEADER */}

      <div className="flex shrink-0 items-center gap-4 border-b border-gray-100 bg-white px-4 py-4 sm:px-7">
        <button type="button" onClick={closeShowReply} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition hover:bg-purple-50 hover:text-purple-600 active:scale-95" aria-label="Go back">
          <FaArrowLeft size={15} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-900">Replies</h2>

            {data.length > 0 && (
              <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-purple-50 px-2 text-[11px] font-semibold text-purple-600">
                {data.length}
              </span>
            )}
          </div>

          <div className="mt-1 flex min-w-0 items-center gap-2">
            <span className="text-xs text-gray-400">Replies to</span>

            <img src={getMediaUrl(comment?.profile_img)} alt={`${comment?.firstname || ""} ${comment?.lastname || ""}`} className="h-5 w-5 shrink-0 rounded-full object-cover" />

            <span className="truncate text-xs font-semibold text-gray-600">
              {comment?.firstname} {comment?.lastname}
            </span>
          </div>
        </div>
      </div>

      {/* CONTENT */}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 sm:px-7">
        {data.length === 0 ? (
          <div className="flex min-h-full flex-col items-center justify-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 text-purple-500">
              <FaRegCommentDots size={24} />
            </div>

            <h3 className="mt-4 text-base font-semibold text-gray-900">No replies yet</h3>
            <p className="mt-1.5 max-w-[280px] text-sm leading-5 text-gray-400">Be the first person to reply to this comment.</p>
          </div>
        ) : (
          <div className="w-full py-2">
            {data.map((reply) => (
              <div key={reply?.reply_id} className="flex gap-3 py-4 sm:gap-4">

                {/* AVATAR */}

                <img src={getMediaUrl(reply?.profile_img)} alt={`${reply?.firstname || ""} ${reply?.lastname || ""}`} className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-gray-100 sm:h-11 sm:w-11" />

                {/* REPLY BODY */}

                <div className="min-w-0 flex-1">

                  {/* USER INFO */}

                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-gray-900">
                        {reply?.firstname} {reply?.lastname}
                      </span>

                      <span className="text-[11px] text-gray-400">
                        <TimeAgo date={reply?.reply_createdAt} />
                      </span>
                    </div>

                    {/* OPTIONS */}

                    <Menu as="div" className="relative shrink-0">
                      <MenuButton className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-50 hover:text-gray-700">
                        <MdMore size={19} />
                      </MenuButton>

                      <Transition as={Fragment} enter="transition ease-out duration-150" enterFrom="opacity-0 scale-95 translate-y-1" enterTo="opacity-100 scale-100 translate-y-0" leave="transition ease-in duration-100" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-1">
                        <MenuItems className="absolute right-0 z-50 mt-1 w-36 origin-top-right rounded-xl border border-gray-100 bg-white p-1.5 shadow-[0_15px_40px_rgba(0,0,0,0.12)] focus:outline-none">
                          <MenuItem>
                            <button type="button" onClick={() => deleteReply(reply?.reply_id)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition data-[focus]:bg-red-50 data-[focus]:text-red-600">
                              <FaTrash size={13} className="text-red-400" />
                              Delete
                            </button>
                          </MenuItem>
                        </MenuItems>
                      </Transition>
                    </Menu>
                  </div>

                  {/* REPLY CONTENT */}

                  <div className="mt-2 min-w-0">
                    {reply?.reply_contentType === "text" && (
                      <div className="inline-block max-w-[560px] rounded-2xl bg-gray-50 px-3.5 py-2.5">
                        <p className="whitespace-pre-wrap break-words text-sm leading-5 text-gray-700">
                          {reply?.reply_content}
                        </p>
                      </div>
                    )}

                    {reply?.reply_contentType === "photo" && <CommentPhoto content={reply?.reply_content} />}

                    {reply?.reply_contentType === "video" && <CommentVideo content={reply?.reply_content} />}

                    {reply?.reply_contentType === "voice" && <CommentVoice content={reply?.reply_content} />}
                  </div>

                  {/* LIKE */}

                  <div className="mt-2 flex items-center">
                    <button type="button" className="flex items-center gap-1.5 rounded-lg px-1 py-1 text-xs font-medium text-gray-400 transition hover:text-purple-600">
                      <img src="/assets/icons/like.svg" alt="" className="h-4 w-4 opacity-70" />
                      Like
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowReply;